/**
 * @file Renders the modal that warns a non-U.S.-person investor about
 * mandatory U.S. tax withholding before they submit their investment.
 */
import { Space } from "antd";
import { WarningOutlined } from "@ant-design/icons";

import { Modal } from "@components/Page";
import { Markdown } from "@components/Text";
import { MODAL_WIDTH } from "@components/Constants";

export const TITLE_NOTICE = "U.S. Tax Withholding Required";

export const TEXT_PRIMARY = "Your answers indicate the beneficial owner of this investment is not a U.S. Person for U.S. federal income tax purposes. U.S. law requires the Partnership to apply mandatory federal tax withholding to income earned by this investment — including income allocated in years when no cash is distributed — and to remit it to the IRS on the beneficial owner's behalf.";

export const TEXT_TREATY = "If you believe an income tax treaty reduces your withholding rate, it is your responsibility to provide valid supporting documentation — the applicable IRS Form W-8 claiming treaty benefits. We do not make treaty determinations on your behalf.";

export const TEXT_DISCLAIMER = "This notice is provided for information only and is not tax advice. Please consult your own tax advisor regarding your specific circumstances.";

const LABEL_REVIEW = "Review my answers";
const LABEL_UNDERSTAND = "I understand";

type Props = {
  isOpen: boolean;
  onReview: () => void;
  onUnderstand: () => void;
};

/**
 * Renders the tax withholding notice, with buttons to review the answers
 * that triggered it or to accept and continue.
 */
const UsTaxWithholdingNoticeModal = ({
  isOpen,
  onReview,
  onUnderstand,
}: Props): JSX.Element => {
  const title = (
    <Space>
      <WarningOutlined />
      {TITLE_NOTICE}
    </Space>
  );

  // Do not set zIndex. Ant Design stacks this modal above a parent Drawer.
  return (
    <Modal
      width={MODAL_WIDTH}
      title={title}
      isOpen={isOpen}
      closable={false}
      onClose={onReview}
      destroyOnClose={true}
      okText={LABEL_UNDERSTAND}
      cancelText={LABEL_REVIEW}
      onOk={onUnderstand}
    >
      <Markdown text={`${TEXT_PRIMARY}\n\n${TEXT_TREATY}\n\n${TEXT_DISCLAIMER}`} />
    </Modal>
  );
};

export default UsTaxWithholdingNoticeModal;
